import React from 'react';
import HeaderWithBackButton from 'app/components/HeaderWithBackButton';
import PageLoader from 'app/components/PageLoader';
import {
  GAME_TYPE_LABELS,
  STATUS_LABELS,
  TRIGGER_TYPE_LABELS,
} from 'app/constants/campaign-constants';
import useCampaignDetail from './useCampaignDetail';

// A `null` field means "explicitly not set" (server confirmed no value); an
// `undefined` field means "not loaded yet" (detail hasn't resolved) — the two
// must read differently so an admin never mistakes a not-yet-fetched screen
// for a genuinely dateless campaign.
function formatDatePart(value, missingLabel) {
  if (value === null) return missingLabel;
  return value || '';
}

// bothNullText, when given, renders once in place of the two missing-date
// labels — used only for the campaign window, where a fully-dateless,
// published campaign has its own meaning ("runs indefinitely") distinct from
// a bill window with no dates set.
function formatWindow(detail, startKey, endKey, bothNullText) {
  if (!detail) return '';
  const start = detail[startKey];
  const end = detail[endKey];
  if (bothNullText && start === null && end === null) return bothNullText;
  return `${formatDatePart(start, 'No start date')} – ${formatDatePart(
    end,
    'No end date'
  )}`;
}

// Read-only campaign detail view (CAMP-09). Presentation-only - all fetch/state
// logic lives in useCampaignDetail. No form inputs, no Save/Edit controls,
// per D-01: the create/edit form ships in a later phase.
const GamificationCampaignDetail = () => {
  const { pageLoading, detail, targetingRows } = useCampaignDetail();

  const campaignWindowText = formatWindow(
    detail,
    'startDate',
    'endDate',
    'Runs while published'
  );
  const billWindowText = formatWindow(detail, 'billStartDate', 'billEndDate');

  return (
    <>
      <div className='cms-content__container'>
        <HeaderWithBackButton title='Campaign Details View' />

        <div className='campaign-detail__basics'>
          <div className='campaign-detail__field'>
            <span className='campaign-detail__label'>Campaign Name</span>
            <span className='campaign-detail__value'>
              {detail?.name || `Campaign #${detail?.id ?? ''}`}
            </span>
          </div>
          <div className='campaign-detail__field'>
            <span className='campaign-detail__label'>Game Type</span>
            <span className='campaign-detail__value'>
              {GAME_TYPE_LABELS[detail?.gameType] || detail?.gameType}
            </span>
          </div>
          <div className='campaign-detail__field'>
            <span className='campaign-detail__label'>Trigger Type</span>
            <span className='campaign-detail__value'>
              {TRIGGER_TYPE_LABELS[detail?.triggerType] || detail?.triggerType}
            </span>
          </div>
          <div className='campaign-detail__field'>
            <span className='campaign-detail__label'>Status</span>
            <span className='campaign-detail__value'>
              {STATUS_LABELS[detail?.status] || detail?.status}
            </span>
          </div>
          <div className='campaign-detail__field'>
            <span className='campaign-detail__label'>Total Points Budget</span>
            <span className='campaign-detail__value'>
              {detail?.totalPointsBudget ?? '—'}
            </span>
          </div>
          <div className='campaign-detail__field'>
            <span className='campaign-detail__label'>Points Per Win</span>
            <span className='campaign-detail__value'>
              {detail?.pointsPerWin ?? '—'}
            </span>
          </div>
          {detail?.targetGender && (
            <div className='campaign-detail__field'>
              <span className='campaign-detail__label'>Gender</span>
              <span className='campaign-detail__value'>{detail.targetGender}</span>
            </div>
          )}
          <div className='campaign-detail__field'>
            <span className='campaign-detail__label'>Always Win</span>
            <span className='campaign-detail__value'>
              {detail?.alwaysWin ? 'Yes' : 'No'}
            </span>
          </div>
          <div className='campaign-detail__field'>
            <span className='campaign-detail__label'>Published</span>
            <span className='campaign-detail__value'>
              {detail?.publish ? 'Yes' : 'No'}
            </span>
          </div>
          <div className='campaign-detail__field'>
            <span className='campaign-detail__label'>Campaign Window</span>
            <span className='campaign-detail__value'>
              {campaignWindowText}
            </span>
          </div>
          <div className='campaign-detail__field'>
            <span className='campaign-detail__label'>Bill Window</span>
            <span className='campaign-detail__value'>
              {billWindowText}
            </span>
          </div>
        </div>

        <div className='campaign-detail__basics'>
          <div className='campaign-detail__field'>
            <span className='campaign-detail__label'>Remaining Vouchers</span>
            <span className='campaign-detail__value'>
              {detail?.remainingVouchers ?? '—'}
              {detail?.lowStock?.vouchers && (
                <span className='campaign-detail__low-stock-warning'>
                  {' '}
                  Low stock — restock the voucher pool or unpublish this
                  campaign before it runs empty.
                </span>
              )}
            </span>
          </div>
          <div className='campaign-detail__field'>
            <span className='campaign-detail__label'>Remaining Points</span>
            <span className='campaign-detail__value'>
              {detail?.remainingPoints == null
                ? 'Not configured'
                : detail.remainingPoints}
              {detail?.lowStock?.points && (
                <span className='campaign-detail__low-stock-warning'>
                  {' '}
                  Low stock — top up the points budget or unpublish this
                  campaign before it runs empty.
                </span>
              )}
            </span>
          </div>
        </div>

        <h4 className='campaign-detail__section-title'>Targeting Summary</h4>
        <table className='table'>
          <thead>
            <tr>
              <th>Mall</th>
              <th>Tier</th>
              <th>Minimum Spend</th>
              <th>Wins per day</th>
              <th>Vouchers</th>
            </tr>
          </thead>
          <tbody>
            {(targetingRows || []).map((row) => (
              <tr key={`${row.mallId}-${row.tierId}`}>
                <td>{row.mallName}</td>
                <td>{row.tierName}</td>
                <td>{row.minSpendThreshold}</td>
                <td>
                  {row.winsPerDay == null ? 'No daily cap' : row.winsPerDay}
                </td>
                <td>
                  {(row.vouchers || []).map((voucher) => (
                    <div key={voucher.id}>
                      {`Voucher #${voucher.id} - remaining: ${voucher.remaining}`}
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pageLoading === true && <PageLoader pageLoading={pageLoading} />}
    </>
  );
};

export default GamificationCampaignDetail;
